import { Box, Button, Field, HStack, Text } from '@chakra-ui/react';
import type {
  BriefCompensation,
  BriefCompensationType,
  BriefFixedPayment,
  BriefTemplateContent,
} from '@fluenceur/types';
import { InfoCircle } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { BriefGiftOfferPicker } from '@/pages/collabs/brief-templates/_shared/BriefGiftOfferPicker';
import { CollabCompensationEstimate } from '@/pages/collabs/brief-templates/_shared/CollabCostEstimate';
import {
  MoneyAmountField,
  type MoneyAmountInputVariant,
} from '@/pages/collabs/brief-templates/editor/sections/MoneyAmountField';

interface CollabTermsCompensationProps {
  content: BriefTemplateContent;
  updateContent: (updater: (prev: BriefTemplateContent) => BriefTemplateContent) => void;
  /** True once the brand has made a deliberate type choice (send gate). */
  compensationConfirmed: boolean;
  onConfirm: () => void;
  showAmountError: boolean;
  showProductError: boolean;
  /** The product is picked but says nothing of what it is worth. */
  showProductValueError: boolean;
}

const TYPES: BriefCompensationType[] = ['fixed_payment', 'gifted_product', 'none'];

/**
 * Per-recipient compensation for the wizard terms step. Reuses the compensation
 * bricks of the template editor (amount + currency group, product picker).
 * Sending requires an explicit choice: picking any pill — including "none",
 * which is a deliberate no-compensation deal — confirms it. Commissions never
 * appear here: affiliation is a separate path.
 */
export const CollabCompensationFields = ({
  compensation,
  onChange,
  compensationConfirmed,
  onConfirm,
  showAmountError,
  showProductError,
  showProductValueError,
  showEstimate = true,
  amountInputVariant,
}: {
  compensation: BriefCompensation;
  showEstimate?: boolean;
  amountInputVariant?: MoneyAmountInputVariant;
  onChange: (compensation: BriefCompensation) => void;
  compensationConfirmed: boolean;
  onConfirm: () => void;
  showAmountError: boolean;
  showProductError: boolean;
  showProductValueError: boolean;
}) => {
  const { t } = useTranslation();

  const setType = (type: BriefCompensationType) => {
    onChange({
      type,
      fixedPayment: type === 'fixed_payment' ? (compensation.fixedPayment ?? null) : null,
      giftedProduct: type === 'gifted_product' ? (compensation.giftedProduct ?? null) : null,
    });
    onConfirm();
  };

  const setFixedPayment = (fixedPayment: BriefFixedPayment | null) =>
    onChange({ type: 'fixed_payment', fixedPayment, giftedProduct: null });

  return (
    <Field.Root>
      <Field.Label fontFamily="heading" fontSize="12.5px" fontWeight="semibold">
        {t('collabs.briefTemplates.sections.compensation.title')}
      </Field.Label>
      <HStack gap="2" wrap="wrap">
        {TYPES.map((type) => {
          const selected = compensationConfirmed && compensation.type === type;
          return (
            <Button
              key={type}
              size="sm"
              variant={selected ? 'solid' : 'outline'}
              colorPalette="primary"
              onClick={() => setType(type)}
            >
              {t(`collabs.briefTemplates.sections.compensation.types.${type}`)}
            </Button>
          );
        })}
      </HStack>

      {compensationConfirmed && compensation.type === 'fixed_payment' && (
        <Box mt="2" w="full">
          <MoneyAmountField
            amountInputVariant={amountInputVariant}
            value={compensation.fixedPayment}
            onChange={setFixedPayment}
            hasError={showAmountError}
          />
          {/* The same estimate as the editor, from the same route: whichever
              way a brief was composed, the total was known before sending. */}
          {showEstimate && (
            <Box mt="2.5">
              <CollabCompensationEstimate compensation={compensation} />
            </Box>
          )}
        </Box>
      )}

      {compensationConfirmed && compensation.type === 'gifted_product' && (
        <Box mt="2" maxW="380px" w="full">
          <BriefGiftOfferPicker
            amountInputVariant={amountInputVariant}
            value={compensation.giftedProduct}
            onChange={(giftedProduct) =>
              onChange({ type: 'gifted_product', fixedPayment: null, giftedProduct })
            }
          />
          {showProductError && (
            <Text fontSize="11.5px" color="fg.error" mt="1.5">
              {t('collabs.briefTemplates.sections.compensation.errorProduct')}
            </Text>
          )}
          {showProductValueError && (
            <Text fontSize="11.5px" color="fg.error" mt="1.5">
              {t('collabs.briefTemplates.sections.compensation.errorProductValue')}
            </Text>
          )}
        </Box>
      )}

      {compensationConfirmed && compensation.type === 'none' && (
        <HStack gap="2" mt="2" color="fg.muted" align="flex-start">
          <InfoCircle width={14} height={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <Text fontSize="11.5px" lineHeight="1.5">
            {t('collabs.briefTemplates.sections.compensation.noneNote')}
          </Text>
        </HStack>
      )}

      {!compensationConfirmed && (
        <Text fontSize="11.5px" color="fg.muted" mt="1.5">
          {t('invitations.modal.collab.wizard.terms.compensationRequired')}
        </Text>
      )}
    </Field.Root>
  );
};

/** Wizard adapter retains its content updates and explicit send confirmation. */
export const CollabTermsCompensation = ({
  content,
  updateContent,
  ...props
}: CollabTermsCompensationProps) => (
  <CollabCompensationFields
    {...props}
    compensation={content.compensation}
    onChange={(compensation) => updateContent((previous) => ({ ...previous, compensation }))}
  />
);
